import React from "react";
import { useLocation } from "react-router-dom";
import { MapPinOff, ShieldAlert, Mail, Phone } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";

// Phase 141 — STRICT territory visibility.
// Supervisors are mapped to zones, tax collectors to wards (or exact mohallas). Until an admin maps
// them, the backend returns nothing for every data view — this gate replaces the whole portal body
// with one clear explanation instead of a wall of empty tables.
//
// `me` is the fresh `/auth/me` profile (see useMe); it carries `territory_status` =
// { applies, mapped, level, zones[], wards[], mohallas[], message }.
// While the profile is still loading (me === null) the children render normally — the API is the
// real guard, the gate is the explanation.

export const NO_TERRITORY_MESSAGE = "No territory assigned — contact your admin";
export const GATED_ROLES = ["tax_collector", "supervisor"];

export function isUnmapped(me) {
  const ts = me?.territory_status;
  return !!(ts && ts.applies && !ts.mapped);
}

export const NoTerritoryState = ({ me, compact = false }) => {
  const role = me?.role;
  const needs = role === "supervisor"
    ? "a Zone"
    : "a Ward (or specific Mohallas within a ward)";
  const who = role === "supervisor" ? "supervisor" : "tax collector";
  return (
    <div className={`flex items-center justify-center ${compact ? "py-8" : "py-10 sm:py-16"}`} data-testid="no-territory-state">
      <Card className="w-full max-w-lg border-dashed">
        <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
          <div className="h-16 w-16 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <MapPinOff className="h-8 w-8" aria-hidden="true" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold font-heading" data-testid="no-territory-title">No territory assigned</h2>
            <p className="text-sm font-medium text-foreground" data-testid="no-territory-message">{NO_TERRITORY_MESSAGE}</p>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Your {who} account is not mapped to {needs} yet. Properties, collections, visits and reports
              stay hidden until your municipal admin maps your territory under <span className="font-medium text-foreground">Users &amp; Staff → Territory</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="gap-1" data-testid="no-territory-role-badge">
              <ShieldAlert className="h-3 w-3" aria-hidden="true" />
              {me?.name || "Staff"}{me?.employee_id ? ` (${me.employee_id})` : ""}
            </Badge>
            {me?.email && (
              <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" aria-hidden="true" />{me.email}</span>
            )}
            {me?.mobile && (
              <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" aria-hidden="true" />{me.mobile}</span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">You can still sign out or open Help &amp; Manual from the header.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export const TerritoryGate = ({ me, loading = false, role, allowPaths = [], children }) => {
  const location = useLocation();
  const gatedRole = GATED_ROLES.includes(role || me?.role);
  // Help & Manual (and any other explicitly allowed page) stays reachable for unmapped staff.
  const onAllowedPath = allowPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  if (loading && gatedRole && !onAllowedPath) return <TerritoryGateSkeleton />;
  if (isUnmapped(me) && !onAllowedPath) return <NoTerritoryState me={me} />;
  return children;
};

// Shown only for the short moment before /auth/me answers, so a mapped collector never sees a flash
// of the "no territory" card and an unmapped one never sees a flash of empty tables / 403 toasts.
export const TerritoryGateSkeleton = () => (
  <div className="space-y-4" data-testid="territory-gate-loading" aria-busy="true" aria-live="polite">
    <Skeleton className="h-8 w-56" />
    <div className="grid gap-3 sm:grid-cols-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
    <Skeleton className="h-48 w-full" />
  </div>
);
